'use client';

import { useEffect, useMemo, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { Camera, X, ArrowRight } from 'lucide-react';
import RelatedLinks from '@/components/seo/RelatedLinks';

/**
 * "Our Work" — photos of customer installations.
 *
 * Photos are rows in `content_media` (folder 'our-work'), uploaded from
 * /admin/gallery, served from the permanent /uploads/gallery store, and read
 * here through the public content API. Tiles are square and use the same
 * `sizes` as the homepage category/product tiles, so the two match exactly.
 * The page used to ship with an empty hard-coded list and a fake "upload your
 * photo" form that submitted nowhere; both are gone.
 */

export interface GalleryPhoto {
  id: number;
  imageUrl: string;
  title: string;
  caption: string;
  room: string;
  productType: string;
  createdAt: string;
}

export default function GalleryClient() {
  const [photos, setPhotos] = useState<GalleryPhoto[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedRoom, setSelectedRoom] = useState('All Rooms');
  const [selectedProduct, setSelectedProduct] = useState('All Products');
  const [selectedPhoto, setSelectedPhoto] = useState<GalleryPhoto | null>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch('/api/v2/content/gallery');
        const json = await res.json();
        const rows: GalleryPhoto[] = json?.data?.photos || [];
        if (alive) setPhotos(rows);
      } catch (e) {
        console.error('Failed to load gallery', e);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  // Filters come from the photos that exist — no hard-coded lists that
  // promise products we don't sell.
  const rooms = useMemo(() => ['All Rooms', ...Array.from(new Set(photos.map((p) => p.room).filter(Boolean)))], [photos]);
  const products = useMemo(() => ['All Products', ...Array.from(new Set(photos.map((p) => p.productType).filter(Boolean)))], [photos]);

  const filtered = photos.filter(
    (p) => (selectedRoom === 'All Rooms' || p.room === selectedRoom) && (selectedProduct === 'All Products' || p.productType === selectedProduct)
  );

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <section className="bg-sb-bg-alt border-b border-sb-border py-14">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-white border border-sb-border text-heading-gold px-4 py-1.5 rounded-full text-sm font-medium mb-5">
              <Camera className="w-4 h-4" /> Our Work
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-heading-charcoal mb-4">Our Work</h1>
            <p className="text-xl text-gray-600">
              Blinds and shades we have measured, made and installed in customers&rsquo; homes.
            </p>
          </div>
        </div>
      </section>

      {/* Filters */}
      {photos.length > 0 && (rooms.length > 2 || products.length > 2) && (
        <section className="bg-white border-b border-sb-border py-4">
          <div className="container mx-auto px-4 flex flex-wrap items-center gap-3">
            {rooms.length > 2 && (
              <select
                id="gallery-room"
                value={selectedRoom}
                onChange={(e) => setSelectedRoom(e.target.value)}
                className="px-3 py-2 border border-sb-border rounded-lg text-sm bg-white"
                aria-label="Filter by room"
              >
                {rooms.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            )}
            {products.length > 2 && (
              <select
                id="gallery-product"
                value={selectedProduct}
                onChange={(e) => setSelectedProduct(e.target.value)}
                className="px-3 py-2 border border-sb-border rounded-lg text-sm bg-white"
                aria-label="Filter by product"
              >
                {products.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            )}
            <span className="text-sm text-gray-500 ml-auto">{filtered.length} photo{filtered.length === 1 ? '' : 's'}</span>
          </div>
        </section>
      )}

      {/* Grid — square tiles, same sizes as the homepage tiles */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          {loading ? (
            <p className="text-center text-gray-500">Loading photos…</p>
          ) : filtered.length === 0 ? (
            <div className="max-w-xl mx-auto text-center py-16">
              <Camera className="w-12 h-12 text-heading-gold mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-heading-charcoal mb-2">Photos coming soon</h2>
              <p className="text-gray-600 mb-6">We are adding installation photos from recent jobs. In the meantime, every product page shows the fabrics in real rooms.</p>
              <Link href="/products" className="inline-flex items-center gap-2 bg-primary-red hover:bg-primary-dark text-white font-semibold px-6 py-3 rounded-lg">
                Browse products <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
              {filtered.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setSelectedPhoto(p)}
                  className="group text-left bg-white border border-sb-border rounded-xl overflow-hidden hover:shadow-lg transition-shadow"
                >
                  <div className="relative aspect-square overflow-hidden bg-gray-100">
                    <Image
                      src={p.imageUrl}
                      alt={p.title || `${p.productType} in ${p.room}`}
                      fill
                      sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 280px"
                      className="object-cover group-hover:scale-[1.03] transition-transform duration-300"
                    />
                  </div>
                  {(p.title || p.room || p.productType) && (
                    <div className="p-3">
                      {p.title && <div className="font-semibold text-heading-charcoal text-sm line-clamp-1">{p.title}</div>}
                      <div className="text-xs text-gray-500">{[p.productType, p.room].filter(Boolean).join(' · ')}</div>
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="py-12 bg-sb-bg-alt border-t border-sb-border">
        <div className="container mx-auto px-4">
          <RelatedLinks
            title="Explore More"
            variant="cards"
            columns={3}
            links={[
              { title: 'Customer Reviews', href: '/reviews', description: 'What customers of our store and online shop say', category: 'Reviews' },
              { title: 'How Motorization Works', href: '/how-motorization-works', description: 'The motor, charging, pairing and smart-home control', category: 'Guide' },
              { title: 'Professional Installation', href: '/installation', description: 'Measured, mounted and set up by our technicians', category: 'Service' },
            ]}
          />
        </div>
      </section>

      {/* Lightbox */}
      {selectedPhoto && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4" onClick={() => setSelectedPhoto(null)}>
          <div className="max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
            <div className="relative aspect-video mb-4">
              <Image src={selectedPhoto.imageUrl} alt={selectedPhoto.title || 'Installation photo'} fill className="object-contain" sizes="(max-width: 1280px) 100vw, 1280px" />
            </div>
            <div className="bg-white rounded-lg p-6">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <h3 className="text-2xl font-bold text-heading-charcoal">{selectedPhoto.title || selectedPhoto.productType || 'Installation'}</h3>
                  <p className="text-sm text-gray-600">{[selectedPhoto.productType, selectedPhoto.room].filter(Boolean).join(' · ')}</p>
                </div>
                <button onClick={() => setSelectedPhoto(null)} className="text-gray-500 hover:text-gray-700" aria-label="Close">
                  <X className="w-6 h-6" />
                </button>
              </div>
              {selectedPhoto.caption && <p className="text-gray-700">{selectedPhoto.caption}</p>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
